/**
 * Custom field for Enable Staging Environment checkbox.
 * Ensures the checkbox is properly checked when pre-filled via formData during redeployment.
 */

import { default as React, useEffect } from 'react';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Box,
} from '@material-ui/core';
import Alert from '@mui/material/Alert';
import { STAGING_ENABLED } from '../components/catalog/microapps/stagingFeatureFlag';

const StagingEnvironmentField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<boolean>) => {
  const helpText = uiSchema?.['ui:help'] as string | undefined;

  // Detect if this is a new deploy or redeploy based on actionType
  // New deploys: actionType is undefined or empty
  // Redeploys: actionType is 'redeploy'
  const actionType = formContext?.formData?.actionType;
  const isNewDeploy = !actionType || actionType !== 'redeploy';

  // Detect if staging is already deployed for this app.
  // When formData === true on a redeploy, the app already has staging enabled
  // (from microapp/stagingEnabled annotation). Once staging is deployed, the
  // user cannot opt out — the checkbox should be checked and disabled.
  const stagingAlreadyDeployed = !isNewDeploy && formData === true;

  useEffect(() => {
    if (!STAGING_ENABLED) {
      onChange(false);
      sessionStorage.removeItem('staging-deploy-initiated');
    } else if (isNewDeploy) {
      // New deploys always go to staging - force checked
      onChange(true);
    } else if (formData !== undefined) {
      onChange(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewDeploy]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow change for redeploys without existing staging
    if (STAGING_ENABLED && !isNewDeploy && !stagingAlreadyDeployed) {
      onChange(event.target.checked);
    }
  };

  // For new deploys: checkbox is always checked and disabled
  // For redeploys with existing staging: checkbox is checked and disabled
  // For redeploys without staging: checkbox is editable
  const isDisabled = !STAGING_ENABLED || isNewDeploy || stagingAlreadyDeployed;

  // Compute checked state without nested ternary
  const computeIsChecked = (): boolean => {
    if (!STAGING_ENABLED) return false;
    if (isNewDeploy) return true;
    return formData === true;
  };
  const isChecked = computeIsChecked();

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            color="primary"
          />
        }
        label={
          STAGING_ENABLED
            ? 'Enable Staging Environment'
            : 'Enable Staging Environment (temporarily disabled)'
        }
      />
      {STAGING_ENABLED && isNewDeploy && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          New deployments always go to staging first. You can promote to
          production from the app details page after validating. Make sure all
          permissions are enabled for the staging environment workload identity
          shown below.{' '}
          <a
            href="/docs/default/component/internal-developer-portal/deploy-internal-apps-user-guide#staging-environment"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit' }}
          >
            Learn more
          </a>
        </Alert>
      )}
      {STAGING_ENABLED && !isNewDeploy && stagingAlreadyDeployed && (
        <FormHelperText>
          Staging is already enabled for this app. This deployment will go to
          staging. You can promote it from the details page to deploy to
          production.{' '}
          <a
            href="/docs/default/component/internal-developer-portal/deploy-internal-apps-user-guide#staging-environment"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit' }}
          >
            Learn more
          </a>
        </FormHelperText>
      )}
      {STAGING_ENABLED && !isNewDeploy && !stagingAlreadyDeployed && (
        <FormHelperText>
          Check to deploy to staging first. You can promote it from the details
          page to deploy to production. Uncheck to deploy directly to
          production.{' '}
          <a
            href="/docs/default/component/internal-developer-portal/deploy-internal-apps-user-guide#staging-environment"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit' }}
          >
            Learn more
          </a>
        </FormHelperText>
      )}
      {!STAGING_ENABLED && (
        <FormHelperText>
          Staging deployments are temporarily disabled. All deployments will go
          directly to production.
        </FormHelperText>
      )}
      {helpText && <FormHelperText>{helpText}</FormHelperText>}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
      {required && formData === undefined && (
        <FormHelperText error>This field is required</FormHelperText>
      )}
    </Box>
  );
};

export const StagingEnvironmentFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'StagingEnvironmentField',
    component: StagingEnvironmentField,
  }),
);
